import React, { useMemo, useEffect, useRef, useState } from "react";
import { MessageBarType, Separator } from "@fluentui/react";
import { Divider, Field, InfoLabel, Spinner, Switch, Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, Button, Dropdown, Option, Input, Text } from "@fluentui/react-components";
import { Dismiss24Regular, InfoFilled } from "@fluentui/react-icons";
import { connect } from "react-redux";
import { useTranslation, Trans } from "react-i18next";

import './PractitionerProtocolConceptForm.Styles.css';


// functions 
import * as ComponentFunctions from './PractitionerProtocolConceptForm.Functions';

// components
import ContractConceptSelectorComponent from "../../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component";

// models
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import { IValueOriginOption } from "../../../Contracts/Concept/ConceptForm/ConceptForm.Functions";
import { CurrencySelectorComponent } from "../../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent";
import MedicalServiceSelectorComponent from "../../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent";
import { AvailableMedicalService } from "../../../../models/Entities/MedicalServices/AvailableMedicalService";
import { NewContractCommissionAgentConcept } from "../../../../models/Entities/Contracts/NewContractCommissionAgentConcept";
import { ContractCommissionAgentConcept } from "../../../../models/Entities/Contracts/ContractCommissionAgentConcept";
import { MessageComponent } from "../../../../commons/component/MessageComponent";


interface FormValidation {
    success: boolean;
    errors: string[];
}

type NumericFieldKey = 'value' | 'remoteValue' | 'additionalValue' | 'failValue';


const PractitionerProtocolConceptForm = (props: ComponentFunctions.Props) => {

    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<
        FormValidation | undefined
    >();

    const containerRef = useRef<HTMLDivElement>(null);
    const initializedContextRef = useRef<string>('');

    const scrollToTop = () => {
        if (containerRef.current) {
            containerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth', // Optional: Adds a smooth scrolling effect
            });
        }
    };

    const { t } = useTranslation();
    const browserLocale = globalThis.navigator?.language;

    const displayNumberFormatter = useMemo(
        () => new Intl.NumberFormat(browserLocale, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
        [browserLocale]
    );

    const editNumberFormatter = useMemo(
        () => new Intl.NumberFormat(browserLocale, { useGrouping: false, minimumFractionDigits: 0, maximumFractionDigits: 2 }),
        [browserLocale]
    );

    const numberParts = useMemo(
        () => new Intl.NumberFormat(browserLocale).formatToParts(12345.6),
        [browserLocale]
    );

    const groupSymbol = numberParts.find((part) => part.type === 'group')?.value ?? ',';
    const decimalSymbol = numberParts.find((part) => part.type === 'decimal')?.value ?? '.';

    // values
    const [value, setValue] = useState<number>(0);
    const [remoteValue, setRemoteValue] = useState<number>(0);
    const [additionalValue, setAdditionalValue] = useState<number>(0);
    const [failValue, setFailValue] = useState<number>(0);
    const [numericInputValues, setNumericInputValues] = useState<Record<NumericFieldKey, string>>({
        value: '0',
        remoteValue: '0',
        additionalValue: '0',
        failValue: '0'
    });


    // form options
    const [conceptsSelected, setConceptsSelected] = useState<ContractConcept[]>([]);
    const [conceptSelected, setConceptSelected] = useState<ContractConcept>();
    const [valueOriginOptionSelected, setValueOriginOptionSelected] = useState<IValueOriginOption>();
    const [currencySelected, setCurrencySelected] = useState<string>();
    const [medicalServiceSelected, setMedicalServiceSelected] = useState<AvailableMedicalService | undefined>();
    const [medicalServiceCleared, setMedicalServiceCleared] = useState<boolean>(false);
    const [takeOnlyItWasDoneByHimself, setTakeOnlyItWasDoneByHimself] = useState<boolean>(false);
    const [takeOnAlreadyRealized, setTakeOnAlreadyRealized] = useState<boolean>(true);
    const [takeOnAlreadyPaid, setTakeOnAlreadyPaid] = useState<boolean>(false);
    const [discountAlreadyPaidToSI, setDiscountAlreadyPaidToSI] = useState<boolean>(false);
    const [settleAtProtocolClose, setSettleAtProtocolClose] = useState<boolean>(false);
    const [discountCostFromConceptCost, setDiscountCostFromConceptCost] = useState<boolean>(false);
    const [payByVisit, setPayByVisit] = useState<boolean>(false);
    const [takeFromGroupedConcepts, setTakeFromGroupedConcepts] = useState<boolean>(false);
    const [currencyCodeToSettle, setCurrencyCodeToSettle] = useState<string | undefined>();

    const formatForDisplay = (numericValue: number): string => displayNumberFormatter.format(numericValue || 0);

    const formatForEdit = (numericValue: number): string => editNumberFormatter.format(numericValue || 0);

    const parseLocalizedNumber = (rawValue: string): number | null => {
        const trimmedValue = rawValue.trim();

        if (!trimmedValue) {
            return 0;
        }

        let normalizedValue = trimmedValue
            .replace(/\s/g, '')
            .replace(new RegExp(`\\${groupSymbol}`, 'g'), '')
            .replace(new RegExp(`\\${decimalSymbol}`, 'g'), '.');

        normalizedValue = normalizedValue.replace(/[^0-9.-]/g, '');

        const firstDotIndex = normalizedValue.indexOf('.');
        if (firstDotIndex >= 0) {
            normalizedValue = `${normalizedValue.slice(0, firstDotIndex + 1)}${normalizedValue.slice(firstDotIndex + 1).replace(/\./g, '')}`;
        }

        const parsedValue = Number(normalizedValue);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    };

    const getNumericFieldValue = (field: NumericFieldKey): number => {
        switch (field) {
            case 'value':
                return value;
            case 'remoteValue':
                return remoteValue;
            case 'additionalValue':
                return additionalValue;
            case 'failValue':
                return failValue;
            default:
                return 0;
        }
    };

    const setNumericFieldValue = (field: NumericFieldKey, numericValue: number) => {
        switch (field) {
            case 'value':
                setValue(numericValue);
                break;
            case 'remoteValue':
                setRemoteValue(numericValue);
                break;
            case 'additionalValue':
                setAdditionalValue(numericValue);
                break;
            case 'failValue':
                setFailValue(numericValue);
                break;
            default:
                break;
        }
    };

    const syncNumericInputValues = (nextValues: { value: number; remoteValue: number; additionalValue: number; failValue: number }) => {
        setNumericInputValues({
            value: formatForDisplay(nextValues.value),
            remoteValue: formatForDisplay(nextValues.remoteValue),
            additionalValue: formatForDisplay(nextValues.additionalValue),
            failValue: formatForDisplay(nextValues.failValue)
        });
    };

    const handleNumericInputChange = (field: NumericFieldKey, rawValue: string) => {
        setNumericInputValues((prev) => ({ ...prev, [field]: rawValue }));

        const parsedValue = parseLocalizedNumber(rawValue);
        if (parsedValue !== null) {
            setNumericFieldValue(field, parsedValue);
        }
    };

    const handleNumericInputFocus = (field: NumericFieldKey, event: React.FocusEvent<HTMLInputElement>) => {
        setNumericInputValues((prev) => ({ ...prev, [field]: formatForEdit(getNumericFieldValue(field)) }));
        event.currentTarget.select();
    };

    const handleNumericInputBlur = (field: NumericFieldKey) => {
        const numericValue = getNumericFieldValue(field);
        setNumericInputValues((prev) => ({ ...prev, [field]: formatForDisplay(numericValue) }));
    };

    // form otions events
    const HandleTakeOnlyItWasDoneByHimself = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setTakeOnlyItWasDoneByHimself(data.checked);
    }

    const HandleTakeOnAlreadyRealized = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setTakeOnAlreadyRealized(data.checked || false);
        setTakeOnAlreadyPaid(!data.checked || false);
    }

    const HandleTakeOnAlreadyPaid = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setTakeOnAlreadyRealized(!data.checked || false);
        setTakeOnAlreadyPaid(data.checked || false);
    }

    const HandleDiscountAlreadyPaidToSI = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setDiscountAlreadyPaidToSI(data.checked || false);
    }

    const HandleSettleAtProtocolClose = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setSettleAtProtocolClose(data.checked || false);
    }

    const HandleDiscountCostFromConceptCost = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setDiscountCostFromConceptCost(data.checked || false);
    }

    const HandlePayByVisit = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        setPayByVisit(data.checked || false);
    }

    useEffect(() => {
        if (!props.isOpen) {
            initializedContextRef.current = '';
            return;
        }

        const contextKey = [
            props.ContractId,
            props.CommissionAgentId ?? 'new-agent',
            props.CommissionAgentConceptId ?? 'new-concept',
            props.CommissionAgentConcept?.id ?? 'no-id'
        ].join('-');

        if (initializedContextRef.current === contextKey) {
            return;
        }

        const initialValues = {
            value: props.CommissionAgentConcept?.value || 0,
            remoteValue: props.CommissionAgentConcept?.remoteValue || 0,
            additionalValue: props.CommissionAgentConcept?.additionalValue || 0,
            failValue: props.CommissionAgentConcept?.failValue || 0
        };

        setValue(initialValues.value);
        setRemoteValue(initialValues.remoteValue);
        setAdditionalValue(initialValues.additionalValue);
        setFailValue(initialValues.failValue);
        syncNumericInputValues(initialValues);
        setValueOriginOptionSelected(ComponentFunctions.GetValueOriginOptionByKey(props.CommissionAgentConcept?.valuationType || 0));
        setCurrencySelected(props.CommissionAgentConcept?.currencyCode || props.Contract?.contractCurrency);
        setMedicalServiceSelected(props.MedicalService);
        setMedicalServiceCleared(false);
        setTakeOnlyItWasDoneByHimself(props.CommissionAgentConcept?.takeOnlyItWasDoneByHimself || false);
        setTakeOnAlreadyRealized(props.CommissionAgentConcept?.takeOnAlreadyRealized ?? true);
        setTakeOnAlreadyPaid(props.CommissionAgentConcept?.takeOnAlreadyPaid ?? false);
        setDiscountAlreadyPaidToSI(props.CommissionAgentConcept?.discountAlreadyPaidToSI ?? false);
        setSettleAtProtocolClose(props.CommissionAgentConcept?.settleAtProtocolClose ?? false);
        setDiscountCostFromConceptCost(props.CommissionAgentConcept?.discountCostFromConceptCost ?? false);
        setPayByVisit(props.CommissionAgentConcept?.payByVisit || false);
        setTakeFromGroupedConcepts(props.CommissionAgentConcept?.takeFromGroupedConcepts ?? false);
        setCurrencyCodeToSettle(props.CommissionAgentConcept?.currencyCodeToSettle || undefined);

        initializedContextRef.current = contextKey;
    }, [
        props.isOpen,
        props.ContractId,
        props.CommissionAgentId,
        props.CommissionAgentConceptId,
        props.CommissionAgentConcept?.id,
        props.Contract?.contractCurrency,
        props.MedicalService
    ]);

    useEffect(() => {
        if (submittedForm && props.SavingSuccessfully) {
            props.dismissPanel();
            resetForm();
        }
    }, [submittedForm, props.SavingSuccessfully]);

    useEffect(() => {
        if (!props.isOpen || medicalServiceSelected || !props.MedicalService || medicalServiceCleared) {
            return;
        }

        if (props.CommissionAgentConcept?.medicalServiceId === props.MedicalService.id) {
            setMedicalServiceSelected(props.MedicalService);
        }
    }, [
        props.isOpen,
        props.MedicalService,
        props.CommissionAgentConcept?.medicalServiceId,
        medicalServiceSelected,
        medicalServiceCleared
    ]);

    const ValidateForm = (): boolean => {
        let validationResult: boolean = true;

        let FormValidation: FormValidation = { success: true, errors: [] };
        setFormValidationStatus(FormValidation);

        const isConceptEffectivelySelected =
            payByVisit ||
            conceptSelected !== undefined ||
            conceptsSelected.length > 0 ||
            (!!props.CommissionAgentConceptId &&
                props.CommissionAgentConcept?.conceptId !== undefined &&
                props.CommissionAgentConcept?.conceptId !== null);

        if (!isConceptEffectivelySelected) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.selectConcept'));
            validationResult = false;
        }

        if (!valueOriginOptionSelected) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.selectValorizationType'));
            validationResult = false;
        }

        if (valueOriginOptionSelected?.isFixedPrice && !currencySelected) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.selectCurrency'));
            validationResult = false;
        }

        if (!valueOriginOptionSelected?.isPriceList && !valueOriginOptionSelected?.isPercentage) {
            if (value === null || value === undefined) {
                FormValidation.errors.push(t('PractitionerProtocolConceptForm.enterValue'));
                validationResult = false;
            }
            if (remoteValue === null || remoteValue === undefined) {
                FormValidation.errors.push(t('PractitionerProtocolConceptForm.enterRemoteValue'));
                validationResult = false;
            }
            if (additionalValue === null || additionalValue === undefined) {
                FormValidation.errors.push(t('PractitionerProtocolConceptForm.enterAdditionalValue'));
                validationResult = false;
            }
            if (failValue === null || failValue === undefined) {
                FormValidation.errors.push(t('PractitionerProtocolConceptForm.enterFailValue'));
                validationResult = false;
            }
        }

        if (selectedMedicalServiceIds.length === 0 && valueOriginOptionSelected?.isPriceList) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.selectMedicalService'));
            validationResult = false;
        }

        if (valueOriginOptionSelected?.isPercentage && (value) > 100) {
            FormValidation.errors.push(t('PractitionerProtocolConceptForm.percentageLimit'));
            validationResult = false;
        }

        if (!validationResult) {
            scrollToTop();
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };

    const resetForm = () => {
        const resetNumericValues = {
            value: 0,
            remoteValue: 0,
            additionalValue: 0,
            failValue: 0
        };

        setValue(resetNumericValues.value);
        setRemoteValue(resetNumericValues.remoteValue);
        setAdditionalValue(resetNumericValues.additionalValue);
        setFailValue(resetNumericValues.failValue);
        syncNumericInputValues(resetNumericValues);
        setConceptsSelected([]);
        setConceptSelected(undefined);
        setValueOriginOptionSelected(ComponentFunctions.ValueOriginOptions[0]);
        setCurrencySelected(props.Contract?.contractCurrency || '');
        setMedicalServiceSelected(undefined);
        setMedicalServiceCleared(false);
        setTakeOnlyItWasDoneByHimself(false);
        setTakeOnAlreadyRealized(true);
        setTakeOnAlreadyPaid(false);
        setDiscountAlreadyPaidToSI(false);
        setPayByVisit(false);
        setCurrencyCodeToSettle(undefined);
        setTakeFromGroupedConcepts(false);
        setSubmittedForm(false);
        setFormValidationStatus(undefined);
    }

    // events
    const HandleCancelForm = () => {
        props.dismissPanel();
    }

    // events
    const HandleSaveForm = () => {
        setSubmittedForm(true);
        if (ValidateForm()) {
            let currentCommissionAgentConcept: NewContractCommissionAgentConcept | ContractCommissionAgentConcept | undefined;
            if (props.CommissionAgentConceptId) {
                // For edit - conceptId should be undefined when payByVisit is true
                const editConcept: ContractCommissionAgentConcept = {
                    id: props.CommissionAgentConceptId,
                    commissionAgentId: props.CommissionAgentId!,
                    contractId: props.ContractId,
                    valuationType: Number(valueOriginOptionSelected?.key) || 0,
                    currencyCode: currencySelected!,
                    value: value,
                    remoteValue: remoteValue,
                    additionalValue: additionalValue,
                    failValue: failValue,
                    medicalServiceId: selectedMedicalServiceId,
                    takeOnlyItWasDoneByHimself: takeOnlyItWasDoneByHimself || false,
                    takeOnAlreadyRealized: takeOnAlreadyRealized || false,
                    takeOnAlreadyPaid: takeOnAlreadyPaid || false,
                    discountAlreadyPaidToSI: discountAlreadyPaidToSI || false,
                    settleAtProtocolClose: settleAtProtocolClose || false,
                    discountCostFromConceptCost: discountCostFromConceptCost || false,
                    payByVisit: payByVisit || false,
                    takeFromGroupedConcepts: takeFromGroupedConcepts,
                    currencyCodeToSettle: takeOnAlreadyPaid ? currencyCodeToSettle : undefined,
                    conceptId: payByVisit
                        ? undefined
                        : (conceptSelected?.id ?? props.CommissionAgentConcept?.conceptId ?? undefined)
                };

                // Only include conceptId if payByVisit is false and a concept is selected
                if (!payByVisit && conceptSelected?.id) {
                    editConcept.conceptId = conceptSelected.id;
                }

                props.UpdateContractCommissionAgentConcept(editConcept as ContractCommissionAgentConcept);
            }
            else {
                // For create - conceptId should be undefined when payByVisit is true
                const createConcept: NewContractCommissionAgentConcept = {
                    commissionAgentId: props.CommissionAgentId!,
                    contractId: props.ContractId,
                    valuationType: Number(valueOriginOptionSelected?.key) || 0,
                    currencyCode: currencySelected!,
                    value: value,
                    remoteValue: remoteValue,
                    additionalValue: additionalValue,
                    failValue: failValue,
                    medicalServiceId: selectedMedicalServiceId,
                    takeOnlyItWasDoneByHimself: takeOnlyItWasDoneByHimself || false,
                    takeOnAlreadyRealized: takeOnAlreadyRealized || false,
                    takeOnAlreadyPaid: takeOnAlreadyPaid || false,
                    discountAlreadyPaidToSI: discountAlreadyPaidToSI || false,
                    settleAtProtocolClose: settleAtProtocolClose || false,
                    discountCostFromConceptCost: discountCostFromConceptCost || false,
                    payByVisit: payByVisit || false,
                    takeFromGroupedConcepts: takeFromGroupedConcepts,
                    currencyCodeToSettle: takeOnAlreadyPaid ? currencyCodeToSettle : undefined,
                    conceptId: conceptsSelected.length > 0 && !payByVisit ? conceptsSelected.map((concept: ContractConcept) => concept.id) : undefined
                };

                props.CreateContractCommissionAgentConcept(props.ContractId, props.CommissionAgentId!, createConcept as NewContractCommissionAgentConcept);
            }
        }
    }

    const contractConceptsSelected = useMemo(() => {
        if (props.CommissionAgentConceptId) {
            if (conceptSelected?.id) {
                return [conceptSelected.id];
            }

            if (
                props.CommissionAgentConcept?.conceptId !== undefined &&
                props.CommissionAgentConcept?.conceptId !== null
            ) {
                return [props.CommissionAgentConcept.conceptId];
            }

            return undefined;
        }

        return conceptsSelected.length > 0
            ? conceptsSelected.map((concept: ContractConcept) => concept.id)
            : undefined;
    }, [props.CommissionAgentConceptId, props.CommissionAgentConcept?.conceptId, conceptSelected, conceptsSelected]);

    const selectedMedicalServiceIds = useMemo(() => {
        if (medicalServiceSelected?.id) {
            return [medicalServiceSelected.id];
        }

        if (!medicalServiceCleared && props.CommissionAgentConcept?.medicalServiceId) {
            return [props.CommissionAgentConcept.medicalServiceId];
        }

        return [];
    }, [medicalServiceSelected?.id, props.CommissionAgentConcept?.medicalServiceId, medicalServiceCleared]);

    const selectedMedicalServiceId = selectedMedicalServiceIds.length > 0
        ? selectedMedicalServiceIds[0]
        : 0;

    const FormField = (
        <div className="PractitionerProtocolConceptFormLayout">
            <section className="PractitionerProtocolConceptSection">
                <div className="PractitionerProtocolConceptSectionHeader">
                    <Text className="PractitionerProtocolConceptSectionTitle" weight="semibold">
                        {t('PractitionerProtocolConceptForm.headerText')}
                    </Text>
                    <Text className="PractitionerProtocolConceptSectionDescription">
                        {t(
                            'PractitionerProtocolConceptForm.selectionSectionHint',
                            'Defina si el calculo se aplica por visita o por concepto y seleccione los conceptos que correspondan.'
                        )}
                    </Text>
                </div>

                <Field
                    className='ToggleInline PractitionerProtocolConceptSwitchField'
                    validationState="none"
                    validationMessageIcon={<InfoFilled />}
                    validationMessage={t('PractitionerProtocolConceptForm.payByVisitInfo')}
                >
                    <Switch
                        label={t('PractitionerProtocolConceptForm.payByVisit')}
                        labelPosition="before"
                        checked={payByVisit}
                        onChange={(ev, data) => HandlePayByVisit(ev, { checked: data.checked })}
                    />
                </Field>

                {!payByVisit && (
                    <div className="PractitionerProtocolConceptFieldGroup">
                        <ContractConceptSelectorComponent
                            onSelectedContractConcept={(contractConcept: ContractConcept[]) => {
                                props.CommissionAgentConceptId
                                    ? setConceptSelected(contractConcept[0])
                                    : setConceptsSelected(contractConcept)
                            }}
                            ContractConceptsSelected={contractConceptsSelected}
                            MultiselectEnable={props.CommissionAgentConceptId ? false : true}
                            noteText={t('PractitionerProtocolConceptForm.conceptNote')}
                            ContractId={props.ContractId}
                            referralDate={undefined}
                        />
                    </div>
                )}

                {!payByVisit && <Field
                    className='ToggleInline PractitionerProtocolConceptSwitchField'
                    validationState="none"
                    validationMessageIcon={<InfoFilled />}
                    validationMessage={t('PractitionerProtocolConceptForm.takeFromGroupedConceptsInfo', 'Toma los valores de conceptos agrupados')}
                >
                    <Switch
                        label={t('PractitionerProtocolConceptForm.takeFromGroupedConcepts', 'Tomar de conceptos agrupados')}
                        labelPosition="before"
                        checked={takeFromGroupedConcepts}
                        onChange={(ev, data) => setTakeFromGroupedConcepts(data.checked)}
                    />
                    {takeFromGroupedConcepts && <>
                    <small style={{ display: 'block', marginTop: 4, color: 'var(--colorNeutralForeground3)' }}>
                        {t('PractitionerProtocolConceptForm.takeFromGroupedConceptsHint', 'Los conceptos agrupados se obtienen de la liquidacion protocolar para cada protocolo, verificar que todos los protocolos esten sincronizados previamente')}
                    </small>
                    <Separator />
                    </>}
                </Field>}
            </section>

            <section className="PractitionerProtocolConceptSection">
                <div className="PractitionerProtocolConceptSectionHeader">
                    <Text className="PractitionerProtocolConceptSectionTitle" weight="semibold">
                        {t('conceptForm.formFields.valorization')}
                    </Text>
                    <Text className="PractitionerProtocolConceptSectionDescription">
                        {t(
                            'PractitionerProtocolConceptForm.valuationSectionHint',
                            'Configure el tipo de valorizacion y los importes para ejecucion presencial, remota y casos fallidos.'
                        )}
                    </Text>
                </div>

                <Field label={t('conceptForm.formFields.valorization')}>
                    <Dropdown
                        value={valueOriginOptionSelected ? t(valueOriginOptionSelected.text) : ''}
                        selectedOptions={valueOriginOptionSelected ? [valueOriginOptionSelected.key.toString()] : []}
                        onOptionSelect={(event, data) => {
                            const selected = ComponentFunctions.ValueOriginOptions.find(opt => opt.key.toString() === data.optionValue);
                            if (selected) setValueOriginOptionSelected(selected);
                        }}
                    >
                        {ComponentFunctions.ValueOriginOptions.map((option: IValueOriginOption) => (
                            <Option key={option.key.toString()} value={option.key.toString()} text={t(option.text) || ''}>
                                {t(option.text)}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>

                {valueOriginOptionSelected?.isFixedPrice &&
                    <div className="PractitionerProtocolConceptFieldGroup">
                        <CurrencySelectorComponent
                            defaultSelected={currencySelected || ''}
                            onCurrencyChange={(currency: string) => setCurrencySelected(currency)}
                            errorMessage={undefined}
                            label={t('settlementDetailForm.formData.currency')}
                            isRequired={true}
                        />
                    </div>
                }

                {!valueOriginOptionSelected?.isPriceList &&
                    <div className="PractitionerProtocolConceptValuesGrid">
                        <Field className="PractitionerProtocolConceptValueField" label={t('conceptForm.formFields.value')}>
                            <Input
                                value={numericInputValues.value}
                                type="text"
                                inputMode="decimal"
                                onFocus={(event) => handleNumericInputFocus('value', event)}
                                onBlur={() => handleNumericInputBlur('value')}
                                onChange={(event, data) => handleNumericInputChange('value', data.value)}
                                contentAfter={valueOriginOptionSelected?.isFixedPrice ? currencySelected : valueOriginOptionSelected?.isPercentage ? '%' : undefined}
                            />
                        </Field>
                        {valueOriginOptionSelected?.isFixedPrice &&
                            <>
                                <Field className="PractitionerProtocolConceptValueField" label={t('PractitionerProtocolConceptForm.remoteValue')}>
                                    <Input
                                        value={numericInputValues.remoteValue}
                                        type="text"
                                        inputMode="decimal"
                                        onFocus={(event) => handleNumericInputFocus('remoteValue', event)}
                                        onBlur={() => handleNumericInputBlur('remoteValue')}
                                        onChange={(event, data) => handleNumericInputChange('remoteValue', data.value)}
                                        contentAfter={valueOriginOptionSelected?.isFixedPrice ? currencySelected : valueOriginOptionSelected?.isPercentage ? '%' : undefined}
                                    />
                                </Field>
                                <Field className="PractitionerProtocolConceptValueField" label={t('PractitionerProtocolConceptForm.additionalValue')}>
                                    <Input
                                        value={numericInputValues.additionalValue}
                                        type="text"
                                        inputMode="decimal"
                                        onFocus={(event) => handleNumericInputFocus('additionalValue', event)}
                                        onBlur={() => handleNumericInputBlur('additionalValue')}
                                        onChange={(event, data) => handleNumericInputChange('additionalValue', data.value)}
                                        contentAfter={valueOriginOptionSelected?.isFixedPrice ? currencySelected : valueOriginOptionSelected?.isPercentage ? '%' : undefined}
                                    />
                                </Field>
                                <Field className="PractitionerProtocolConceptValueField" label={t('PractitionerProtocolConceptForm.failValue')}>
                                    <Input
                                        value={numericInputValues.failValue}
                                        type="text"
                                        inputMode="decimal"
                                        onFocus={(event) => handleNumericInputFocus('failValue', event)}
                                        onBlur={() => handleNumericInputBlur('failValue')}
                                        onChange={(event, data) => handleNumericInputChange('failValue', data.value)}
                                        contentAfter={valueOriginOptionSelected?.isFixedPrice ? currencySelected : valueOriginOptionSelected?.isPercentage ? '%' : undefined}
                                    />
                                </Field>
                            </>
                        }
                    </div>
                }

                {valueOriginOptionSelected?.isPriceList &&
                    <div className="PractitionerProtocolConceptFieldGroup">
                        <MedicalServiceSelectorComponent
                            onSelectedServices={(services: AvailableMedicalService[] | undefined) => {
                                if (services && services.length > 0) {
                                    setMedicalServiceSelected(services[0]);
                                    setMedicalServiceCleared(false);
                                    return;
                                }

                                setMedicalServiceSelected(undefined);
                                setMedicalServiceCleared(true);
                            }}
                            noteText={t('PractitionerProtocolConceptForm.medicalServiceNote')}
                            MedicalServicesSelectedIds={selectedMedicalServiceIds}
                            MultiselectEnable={false}
                        />
                    </div>
                }
            </section>

            <section className="PractitionerProtocolConceptSection">
                <div className="PractitionerProtocolConceptSectionHeader">
                    <Text className="PractitionerProtocolConceptSectionTitle" weight="semibold">
                        {t('PractitionerProtocolConceptForm.applicationRules', 'Reglas de aplicacion')}
                    </Text>
                    <Text className="PractitionerProtocolConceptSectionDescription">
                        {t(
                            'PractitionerProtocolConceptForm.rulesSectionHint',
                            'Defina como se toma este concepto durante la liquidacion segun su estado y autoria.'
                        )}
                    </Text>
                </div>

                <div className="PractitionerProtocolConceptSwitchGroup">
                    <Field
                        className='ToggleInline PractitionerProtocolConceptSwitchField'
                        validationState="none"
                        validationMessageIcon={<InfoFilled />}
                        validationMessage={t('PractitionerProtocolConceptForm.takeOnlyByPractitionerInfo')}
                    >
                        <Switch
                            label={t('PractitionerProtocolConceptForm.takeOnlyByPractitioner')}
                            labelPosition="before"
                            checked={takeOnlyItWasDoneByHimself}
                            onChange={(ev, data) => HandleTakeOnlyItWasDoneByHimself(ev, { checked: data.checked })}
                        />
                    </Field>
                    <Field
                        className='ToggleInline PractitionerProtocolConceptSwitchField'
                        validationState="none"
                        validationMessageIcon={<InfoFilled />}
                        validationMessage={t('PractitionerProtocolConceptForm.settleOnRealizedInfo')}
                    >
                        <Switch
                            label={t('PractitionerProtocolConceptForm.settleOnRealized')}
                            labelPosition="before"
                            checked={takeOnAlreadyRealized}
                            onChange={(ev, data) => HandleTakeOnAlreadyRealized(ev, { checked: data.checked })}
                        />
                    </Field>
                    <Field
                        className='ToggleInline PractitionerProtocolConceptSwitchField'
                        validationState="none"
                        validationMessageIcon={<InfoFilled />}
                        validationMessage={t('PractitionerProtocolConceptForm.settleOnPaidInfo')}
                    >
                        <Switch
                            label={t('PractitionerProtocolConceptForm.settleOnPaid')}
                            labelPosition="before"
                            checked={takeOnAlreadyPaid}
                            onChange={(ev, data) => HandleTakeOnAlreadyPaid(ev, { checked: data.checked })}
                        />
                        {takeOnAlreadyPaid && (
                            <>
                                <div className="PractitionerProtocolConceptFieldGroup">
                                    <CurrencySelectorComponent
                                        defaultSelected={currencyCodeToSettle || ''}
                                        onCurrencyChange={(currency: string) => setCurrencyCodeToSettle(currency)}
                                        errorMessage={undefined}
                                        label={t('PractitionerProtocolConceptForm.currencyCodeToSettle', 'Moneda a liquidar')}
                                        isRequired={false}
                                    />
                                </div>
                                <Divider style={{ width: 'auto', margin: '10px 0' }} />
                            </>
                        )}
                    </Field>

                    <Field
                        className='ToggleInline PractitionerProtocolConceptSwitchField'
                        validationState="none"
                        validationMessageIcon={<InfoFilled />}
                        validationMessage={t('PractitionerProtocolConceptForm.settleAtProtocolCloseInfo', 'El concepto se liquida recién cuando el protocolo se cierra')}
                    >
                        <Switch
                            label={t('PractitionerProtocolConceptForm.settleAtProtocolClose', 'Liquidar al cierre del protocolo')}
                            labelPosition="before"
                            checked={settleAtProtocolClose}
                            onChange={(ev, data) => HandleSettleAtProtocolClose(ev, { checked: data.checked })}
                        />
                    </Field>

                    <Field
                        className='ToggleInline PractitionerProtocolConceptSwitchField'
                        validationState="none"
                        validationMessageIcon={<InfoFilled />}
                        validationMessage={t('PractitionerProtocolConceptForm.discountPaidByTeamInfo')}
                    >
                        <Switch
                            label={t('PractitionerProtocolConceptForm.discountPaidByTeam')}
                            labelPosition="before"
                            checked={discountAlreadyPaidToSI}
                            onChange={(ev, data) => HandleDiscountAlreadyPaidToSI(ev, { checked: data.checked })}
                        />
                    </Field>

                    <Field
                        className='ToggleInline PractitionerProtocolConceptSwitchField'
                        validationState="none"
                        validationMessageIcon={<InfoFilled />}
                        validationMessage={t('PractitionerProtocolConceptForm.discountCostFromConceptCostInfo', 'El costo liquidado al comisionista se descuenta del costo del concepto')}
                    >
                        <Switch
                            label={t('PractitionerProtocolConceptForm.discountCostFromConceptCost', 'Descontar el costo del concepto')}
                            labelPosition="before"
                            checked={discountCostFromConceptCost}
                            onChange={(ev, data) => HandleDiscountCostFromConceptCost(ev, { checked: data.checked })}
                        />
                    </Field>
                </div>
            </section>
        </div>
    );

    const FormValidationStatusMessage = (FormValidationStatus?.errors?.length ? FormValidationStatus?.errors?.length > 0 : false) && (
        <>
            <MessageComponent
                message={t('PractitionerProtocolConceptForm.validationError')}
                type={MessageBarType.error}
                subMessage={t('PractitionerProtocolConceptForm.validationErrorSub')}
                itemList={FormValidationStatus?.errors || []}
            />
        </>
    );

    const SaveErrorStatusMessage = (props.FailOnSaving && props.ErrorOnSaving) && (
        <>
            <MessageComponent
                message={t('PractitionerProtocolConceptForm.saveError')}
                type={MessageBarType.error}
                subMessage={props.ErrorOnSaving.ErrorMessage}
                itemList={props.ErrorOnSaving?.Errors || []}
            />
        </>
    );

    // footer
    const FooterContent = (
        <div className="PractitionerProtocolConceptFooter">
            <Button onClick={HandleCancelForm} appearance="secondary" disabled={props.isSaving} type="button">
                <Trans i18nKey={'conceptForm.footer.cancelButton'} />
            </Button>
            <Button
                onClick={HandleSaveForm}
                disabled={props.isSaving}
                appearance="primary"
                type="button"
                icon={props.isSaving ? <Spinner size="extra-tiny" /> : undefined}
            >
                {props.isSaving ? t('common.saving') : <Trans i18nKey={'conceptForm.footer.saveButton'} />}
            </Button>
        </div>
    );


    return (
        <Drawer
            type="overlay"
            separator
            open={props.isOpen}
            position="end"
            size="medium"
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Close"
                            icon={<Dismiss24Regular />}
                            onClick={HandleCancelForm}
                        />
                    }
                >
                    {t('PractitionerProtocolConceptForm.headerText')}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <div ref={containerRef} className="PractitionerProtocolConceptScrollContainer">
                    {FormValidationStatusMessage}
                    {SaveErrorStatusMessage}
                    <Divider className="PractitionerProtocolConceptMessagesDivider" />
                    {FormField}
                </div>
            </DrawerBody>
            <DrawerFooter>
                {FooterContent}
            </DrawerFooter>
        </Drawer>
    );
}

export default connect(
    ComponentFunctions.mapStateToProps,
    (ComponentFunctions.mapDispatchToProps)
)(PractitionerProtocolConceptForm as any);